import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShieldStarIcon as ShieldStar, UsersThreeIcon as UsersThree, PulseIcon as Pulse } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

/**
 * Three-position mode switcher rendered in the AppShell header for
 * admins who also use the kennel-owner workspace.
 *
 * Segments (each is a one-click destination):
 *  • Kennel     → /dashboard       (personal kennel workspace)
 *  • Operations → /admin           (operations hub — daily health checks)
 *  • Command    → /admin/console   (full admin console — every tool)
 *
 * Scratchpad badges: each non-active segment gets a small severity dot
 * when it has something needing attention.
 *   • Operations: lights up when /admin/ops/summary returns any
 *     critical-severity card (stuck payments, paid-but-unfulfilled, etc.)
 *   • Command:    lights up when /admin/ops/deploy-status reports
 *     commits_since_last_deploy > 0 (you're sitting on un-deployed fixes).
 *
 * Hidden for non-admins so regular users see no admin-only chrome.
 */

const SEGMENTS = [
  {
    key: "users",
    label: "Users",
    to: "/admin/users-ops",
    icon: UsersThree,
    matches: (path) => path.startsWith("/admin/users-ops") || path.startsWith("/admin/users") || path.startsWith("/admin/personas") || path.startsWith("/admin/chat-reports"),
  },
  {
    key: "operations",
    label: "Operations",
    to: "/admin",
    icon: Pulse,
    matches: (path) => path === "/admin" || path.startsWith("/admin/operations"),
  },
  {
    key: "command",
    label: "Command",
    to: "/admin/console",
    icon: ShieldStar,
    matches: (path) =>
      path.startsWith("/admin") &&
      !(path === "/admin" || path.startsWith("/admin/operations") ||
        path.startsWith("/admin/users") || path.startsWith("/admin/personas") ||
        path.startsWith("/admin/chat-reports")),
  },
];

export default function AdminModeSwitcher() {
  const { user } = useAuth() || {};
  const location = useLocation();
  const [opsSummary, setOpsSummary] = useState(null);
  const [usersSummary, setUsersSummary] = useState(null);
  const [deployStatus, setDeployStatus] = useState(null);
  const lastCriticalRef = React.useRef(0);
  const lastUsersAttnRef = React.useRef(Infinity);
  const isAdmin = user?.role === "admin";

  // Poll lightweight ops + deploy endpoints every 60s so the dots stay
  // honest without hammering the API. Failures are silent — a dot just
  // doesn't appear, which is the safe fallback.
  //
  // Also fire a native browser notification on the 0→1+ critical
  // transition so an admin viewing another tab gets pinged the moment
  // something breaks for a paying customer. Idempotent: we only ping
  // on the rising edge.
  useEffect(() => {
    if (!isAdmin) return undefined;
    let cancelled = false;
    const load = () => {
      api.get("/admin/ops/summary").then((r) => {
        if (cancelled) return;
        setOpsSummary(r.data);
        const newCrit = (r.data?.cards || []).filter((c) => c.severity === "critical").length;
        if (newCrit > lastCriticalRef.current && lastCriticalRef.current >= 0) {
          // Rising edge — ping if permission was previously granted.
          if (typeof Notification !== "undefined" && Notification.permission === "granted") {
            try {
              const labels = (r.data?.cards || [])
                .filter((c) => c.severity === "critical")
                .map((c) => `${c.label} (${c.count})`)
                .slice(0, 3)
                .join(" · ");
              new Notification("Armstrong K9 — operations alert", {
                body: labels || `${newCrit} critical signal(s) on the Operations dashboard.`,
                tag: "ops-critical",
                requireInteraction: false,
              });
            } catch {
              // Notification constructor can throw on some platforms — silent fail.
            }
          }
        }
        lastCriticalRef.current = newCrit;
      }).catch(() => {});
      api.get("/admin/ops/deploy-status", { params: { limit: 25 } })
        .then((r) => !cancelled && setDeployStatus(r.data)).catch(() => {});
      api.get("/admin/users-ops/summary")
        .then((r) => {
          if (cancelled) return;
          setUsersSummary(r.data);
          const attn = (r.data?.cards || []).filter(
            (c) => c.severity === "critical" || c.severity === "warn"
          );
          if (attn.length > lastUsersAttnRef.current) {
            if (typeof Notification !== "undefined" && Notification.permission === "granted") {
              try {
                const labels = attn.map((c) => `${c.label} (${c.count})`).slice(0, 3).join(" · ");
                new Notification("Armstrong K9 — member alert", {
                  body: labels || `${attn.length} member signal(s) need review.`,
                  tag: "users-attention",
                  requireInteraction: false,
                });
              } catch { /* platform quirks — silent */ }
            }
          }
          lastUsersAttnRef.current = attn.length;
        }).catch(() => {});
    };
    load();
    const id = setInterval(load, 60_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [isAdmin]);

  if (!isAdmin) return null;

  const opsCriticalCount = (opsSummary?.cards || []).filter((c) => c.severity === "critical").length;
  const opsWarnCount = (opsSummary?.cards || []).filter((c) => c.severity === "warn").length;
  const commitsBehind = deployStatus?.commits_since_last_deploy || 0;
  const usersAttention = (usersSummary?.cards || []).filter(
    (c) => c.severity === "critical" || c.severity === "warn"
  ).length;

  // Each badge: { color: "critical" | "warn", count: number | null, title: string }
  // count=null → render as a plain dot, count=N → render a small numeric pill.
  const badges = {
    operations:
      opsCriticalCount > 0
        ? { color: "critical", count: opsCriticalCount, title: `${opsCriticalCount} critical signal${opsCriticalCount === 1 ? "" : "s"}` }
        : opsWarnCount > 0
        ? { color: "warn", count: opsWarnCount, title: `${opsWarnCount} warning${opsWarnCount === 1 ? "" : "s"}` }
        : null,
    command:
      commitsBehind > 0
        ? { color: "warn", count: Math.min(commitsBehind, 99), title: `${commitsBehind} commit${commitsBehind === 1 ? "" : "s"} pending redeploy` }
        : null,
    users:
      usersAttention > 0
        ? { color: "warn", count: usersAttention, title: `${usersAttention} member signal${usersAttention === 1 ? "" : "s"} to review` }
        : null,
  };

  return (
    <div
      role="tablist"
      aria-label="Workspace switcher"
      data-testid="admin-mode-switcher"
      className="hidden md:inline-flex items-center gap-0.5 rounded-full bg-[#0E0E0E]/5 border border-[#C9A227]/40 p-0.5"
    >
      {SEGMENTS.map((seg) => {
        const active = seg.matches(location.pathname);
        const Icon = seg.icon;
        const badge = badges[seg.key];
        // Only show the badge on the OTHER tabs — once you're on the
        // tab, the badge is redundant (the page itself shows the issues).
        const showBadge = !!badge && !active;
        return (
          <Link
            key={seg.key}
            to={seg.to}
            role="tab"
            aria-selected={active}
            data-testid={`mode-switcher-${seg.key}`}
            title={badge?.title || `Go to ${seg.label}`}
            className={[
              "relative inline-flex items-center gap-1.5 rounded-full h-7 px-2.5 2xl:px-3",
              "text-[10px] font-bold uppercase tracking-[0.14em] transition-colors",
              active
                ? "bg-[#C9A227] text-ink shadow-[inset_0_0_0_1px_rgba(0,0,0,0.06)]"
                : "text-[#6B6B6B] hover:bg-[#C9A227]/15 hover:text-ink",
            ].join(" ")}
          >
            <Icon size={11} weight={active ? "fill" : "duotone"} />
            <span className="hidden 2xl:inline">{seg.label}</span>
            {showBadge && (
              <span
                data-testid={`mode-switcher-${seg.key}-badge`}
                className={[
                  "ml-0.5 inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full text-[9px] font-bold leading-none",
                  badge.color === "critical"
                    ? "bg-[#C1272D] text-white"
                    : "bg-[#8A6A00] text-white",
                ].join(" ")}
              >
                {badge.count}
              </span>
            )}
          </Link>
        );
      })}
    </div>
  );
}
